"use client";

import React from "react";
import { Wind, ArrowRight, ShieldCheck, Gauge, Layers, FileText, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const highlights = [{
  title: "Facas de ar WindJet®",
  description: "Cortinas de ar homogéneas para remoção instantânea de humidade e limpeza contínua em esteiras de transporte.",
  icon: Wind
}, {
  title: "Eficiência energética",
  description: "Sistemas concebidos para reduzir o consumo de ar comprimido até 60% face a tubos perfurados convencionais.",
  icon: Gauge
}, {
  title: "Qualidade de acabamento",
  description: "Controlo rigoroso do impacto superficial sem danificar embalagens, peças usinadas ou produtos sensíveis.",
  icon: ShieldCheck
}];
const technicalSpecs = ["Pressão de trabalho otimizada", "Homogeneidade de caudal laminar", "Baixo nível de ruído operacional", "Compatibilidade com normas higiénicas"];
export default function Blowoff() {
  return <section id="dryingblowoffhero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-20 md:py-28 lg:py-32">
      {/* Decorative background grid and gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(215_90%_45%/0.22),transparent_50%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,hsl(215_90%_35%/0.18),transparent_50%)]" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-10 bg-[linear-gradient(to_right,transparent,hsl(210_20%_98%/0.15))]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          
          {/* Main Hero Copy */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="bg-secondary/20 text-secondary-foreground border border-secondary/30 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                <Sparkles className="size-3.5 mr-1" />
                Soluções Industriais Eurocontrol
              </Badge>
              <span className="text-xs text-primary-foreground/70 tracking-wide font-mono">
                Spraying Systems Co.
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tight text-primary-foreground sm:text-5xl lg:text-6xl font-default">
                Secagem & Blow-off
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/80 leading-relaxed max-w-2xl font-sans">
                Remova humidade, resíduos, líquidos e excesso de produto em linhas contínuas com tecnologia de ponta em facas de ar, bicos calibrados e pacotes sopradores de baixo consumo.
              </p>
            </div>

            {/* Quick Spec List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {technicalSpecs.map((spec, index) => <div key={index} data-index={index} className="flex items-center gap-2.5 text-sm text-primary-foreground/85">
                  <CheckCircle2 className="size-4 shrink-0 text-secondary" />
                  <span>{spec}</span>
                </div>)}
            </div>

            {/* CTA Actions */}
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md font-medium">
                <Link to="/ContactsPage">
                  <span>Consulte os nossos especialistas</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-primary/40 border-primary-foreground/20 text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground shadow-xs font-medium">
                <Link to="/CatalogsPage">
                  <FileText className="size-4 mr-2" />
                  <span>Ver catálogos técnicos</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Interactive Feature Cards Column */}
          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-2xl border border-primary-foreground/15 bg-card/10 backdrop-blur-md p-6 shadow-xl space-y-4">
              <div className="flex items-center justify-between border-b border-primary-foreground/15 pb-4">
                <div className="flex items-center gap-2">
                  <Layers className="size-5 text-secondary" />
                  <h2 className="text-base font-semibold text-primary-foreground">
                    Princípios de operação
                  </h2>
                </div>
                <span className="text-xs text-primary-foreground/70 font-mono">
                  Sistemas standard & custom
                </span>
              </div>

              <div className="space-y-3">
                {highlights.map((item, index) => {
                const Icon = item.icon;
                return <Card key={index} data-index={index} className="border border-primary-foreground/10 bg-primary-foreground/5 shadow-xs transition-all duration-200 hover:border-secondary/40 hover:bg-primary-foreground/10">
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="rounded-lg bg-secondary/20 p-2.5 text-secondary shrink-0">
                          <Icon className="size-5" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-sm font-semibold text-primary-foreground">
                            {item.title}
                          </h3>
                          <p className="text-xs text-primary-foreground/75 leading-normal">
                            {item.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>;
              })}
              </div>

              <div className="pt-2">
                <div className="rounded-lg bg-primary-foreground/10 p-3.5 border border-primary-foreground/10 flex items-center justify-between text-xs text-primary-foreground/80">
                  <span className="font-medium">Precisa de auditoria na linha existente?</span>
                  <Link to="/ContactsPage" className="text-secondary font-semibold hover:underline inline-flex items-center gap-1">
                    Pedir avaliação <ArrowRight className="size-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}